import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BrandSplash from './BrandSplash';
import './LandingSplash.css';

const SEEN_KEY = 'lmf.splash.seen.v1';
const FLY_MS = 900;
const WAIT_FOR_HEADER_MS = 2500;
const ACTIVE_CLASS = 'lmf-splash-active';

/* Used when the browser blocks storage, so the splash still plays only once per page session. */
let seenInMemory = false;

const readSeen = () => {
  if (seenInMemory) return true;
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const markSeen = () => {
  seenInMemory = true;
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* storage blocked: the in-memory flag covers this session */
  }
};

const rectOf = (element) => element?.getBoundingClientRect();

/** Translate + uniform-scale `element` so its box lands on `target` (both measured before the move). */
const flyTo = (element, target) => {
  const from = rectOf(element);
  const to = rectOf(target);
  if (!from || !to || !from.width || !to.width) return false;
  const scale = to.width / from.width;
  const dx = to.left - from.left;
  // The origin is the element's left-centre, so scaling keeps its centre line: match centre lines.
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  element.style.transformOrigin = '0 50%';
  element.style.transition = `transform ${FLY_MS}ms cubic-bezier(.65,0,.25,1)`;
  element.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
  return true;
};

/**
 * First-visit brand splash for the landing page. It plays the animated lockup once per browser
 * (a localStorage flag set as it starts, so a refresh mid-way never repeats it), then the logo mark
 * and the name glide up to the header's logo and name and the veil fades out. While it plays the
 * header's own logo is hidden so there is never a duplicate. If the header is not there yet (its
 * settings are still loading) the splash waits for it, and simply fades out if it never appears.
 */
export default function LandingSplash() {
  const [phase, setPhase] = useState(() => (readSeen() ? 'done' : 'play'));
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (phase === 'done') return undefined;
    markSeen();
    document.documentElement.classList.add(ACTIVE_CLASS);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.classList.remove(ACTIVE_CLASS);
      document.body.style.overflow = previousOverflow;
    };
  }, [phase]);

  const handleTimeline = useCallback(() => setPhase('fly'), []);

  useEffect(() => {
    if (phase !== 'fly') return undefined;
    let cancelled = false;
    let finishTimer;
    const started = performance.now();
    const begin = () => {
      const root = rootRef.current;
      const logo = document.querySelector('.landing-header .lmf-logo');
      const markTarget = logo?.querySelector('[data-brand-mark]');
      const nameTarget = logo?.querySelector('[data-brand-name]');
      const mark = root?.querySelector('.bsplash-mark');
      const name = root?.querySelector('.bsplash-line');
      if (!root || !markTarget || !nameTarget || !mark || !name) {
        if (performance.now() - started < WAIT_FOR_HEADER_MS) {
          requestAnimationFrame(() => !cancelled && begin());
          return;
        }
        root?.classList.add('is-leaving');
        finishTimer = setTimeout(() => !cancelled && setPhase('done'), 400);
        return;
      }
      root.classList.add('is-flying');
      flyTo(mark, markTarget);
      flyTo(name, nameTarget);
      finishTimer = setTimeout(() => !cancelled && setPhase('done'), FLY_MS + 80);
    };
    begin();
    return () => {
      cancelled = true;
      clearTimeout(finishTimer);
    };
  }, [phase]);

  if (phase === 'done') return null;
  return (
    <div ref={rootRef} className="lmf-landing-splash" aria-hidden={phase === 'fly' ? true : undefined}>
      <BrandSplash onDone={handleTimeline} />
    </div>
  );
}
